'use client';
import { Button } from '@components/Button';
import EVImg from '@components/EVImg/EVImg';
import { ErrorMessage, FormField, FormLabel, InputField } from '@components/FormFields';
import Modal from '@components/Modal';
import Portal from '@components/Modal/ModalPortal';
import { Status } from '@icon/index';
import { usePathname } from 'next/navigation';
import React, { FC, useEffect, useRef, useState } from 'react';
import {
  EmailField,
  ImageWrapper,
  ModalButtonWrapper,
  ModalButtonWrapperFixed,
  ModalHeader,
  ModalInner,
  ModalText,
  SuccessText,
  SuccessWrapper,
  SVGWrapper,
  TopDefault,
} from './styles';
//Data
import { PlainText } from '@components/Layout/GlobalStyle';
import RichText from '@components/RichText';
import { getEntries } from '@data/preview';
import { CreateLoftyLeadDto } from '@engelvoelkersamericas/api';
import { useTranslations } from 'next-intl';
import { apiClient } from '../../api-client';

interface BlogSubscribeBtnProps {
  chimeId: string;
  chimeType: string;
  locale?: string;
}

// COMPONENT STARTS HERE
const BlogSubscribeBtn: FC<BlogSubscribeBtnProps> = ({ chimeId, chimeType, locale = 'en' }) => {
  const t = useTranslations();
  const pathname = usePathname();
  const americasNewsletterModalContentfulId = '7EIWLek5oMlyqcGpZcTVUw';
  const fetchAmericasNewsletterModalData = async () => {
    try {
      let componentData = await getEntries(americasNewsletterModalContentfulId, locale);
      return componentData;
    } catch (e) {
      console.log(e);
    }
  };
  const [newsletterModalData, setNewsletterModalData] = useState<any>({});
  useEffect(() => {
    fetchAmericasNewsletterModalData().then((data) => {
      setNewsletterModalData(data.fields);
    });
    setURL(`${window.location.origin}${pathname}`);
  }, []);

  // const [showModal, setShowModal] = useState<boolean>(false);
  const [emailError, setEmailError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [url, setURL] = React.useState('');
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showUnsuccessful, setShowUnsuccessful] = useState<boolean>(false);

  //setup the modal
  let modal = useRef<any>(null);

  const openModalSection = () => {
    modal.current?.setOpen(true);
  };

  const closeAllModals = () => {
    modal.current?.toggle(false);
  };

  const handleInputChange = (e) => {
    if (e?.target?.id == 'firstName') {
      setFirstNameError('');
    }

    if (e?.target?.id == 'lastName') {
      setLastNameError('');
    }

    if (e?.target?.id == 'email') {
      setEmailError('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFirstNameError('');
    setLastNameError('');
    setEmailError('');

    //get the form data
    const data = {
      firstName: event?.target?.firstName?.value,
      lastName: event?.target?.lastName?.value,
      email: event?.target?.email?.value,
    };

    //validations
    let formIsValid = true;
    setFirstNameError('');
    if (!data.firstName) {
      formIsValid = false;
      setFirstNameError(t('Validation.firstNameRequired'));
    }

    if (!data.email) {
      formIsValid = false;
      setEmailError(t('Validation.emailRequired'));
    }

    if (typeof data.email !== 'undefined') {
      let lastAtPos = data.email.lastIndexOf('@');
      let lastDotPos = data.email.lastIndexOf('.');

      if (
        !(
          lastAtPos < lastDotPos &&
          lastAtPos > 0 &&
          data.email.indexOf('@@') == -1 &&
          lastDotPos > 2 &&
          data.email.length - lastDotPos > 2
        )
      ) {
        formIsValid = false;
        setEmailError(t('Email is not valid'));
      }
    }

    if (formIsValid) {
      const chimeObj: CreateLoftyLeadDto = {
        userFirstName: data.firstName,
        userLastName: data.lastName,
        userEmail: data.email,
        chimeId: chimeId ? parseInt(chimeId, 10) : undefined,
        type: chimeType as 'Shop' | 'Agent' | 'EV Global',
      };
      if (url) {
        chimeObj.url = url;
      }

      try {
        const res = await apiClient.createLoftyLeads({
          createLoftyLeadDto: chimeObj,
        });

        if (res.status === 201) {
          setShowSuccess(true);
        } else {
          setShowUnsuccessful(true);
        }
      } catch (err) {
        setShowUnsuccessful(true);
      }
    }
  };
  console.log('chimeId is ready: ', chimeId);
  return (
    <>
      <Button
        onClickHandler={() => openModalSection()}
        variant="secondary"
        label={t('Subscribe')}
        arrow={false}
      />

      <Portal>
        {' '}
        {/* // Portal will render at the bottom of the page, look at page.tsx for implementation */}
        <Modal
          top={
            <TopDefault onClick={() => closeAllModals()}>
              <Button
                variant="iconOnly"
                onClickHandler={() => closeAllModals()}
                arrow={false}
                label={t('Close')}
                icon="Close"
                iconSize="36px"
              />
            </TopDefault>
          }
          center={
            <ModalInner>
              {!showSuccess && !showUnsuccessful ? (
                <FormSection
                  emailError={emailError}
                  firstNameError={firstNameError}
                  lastNameError={lastNameError}
                  handleInputChange={handleInputChange}
                  handleSubmit={handleSubmit}
                  newsletterModalData={newsletterModalData}
                />
              ) : showUnsuccessful ? (
                <UnsuccessfulSection newsletterModalData={newsletterModalData} />
              ) : (
                <SuccessSection newsletterModalData={newsletterModalData} />
              )}
            </ModalInner>
          }
          bottom={
            !showSuccess && !showUnsuccessful ? (
              <ModalButtonWrapperFixed>
                <ModalButtonWrapper>
                  <Button
                    type="submit"
                    variant="primaryRed"
                    label={t('Subscribe')}
                    arrow={false}
                    form="subscribe"
                  />
                </ModalButtonWrapper>
              </ModalButtonWrapperFixed>
            ) : (
              <></>
            )
          }
          ref={modal}
        />
      </Portal>
    </>
  );
};

interface FormSectionProps {
  handleInputChange: (i) => void;
  handleSubmit: (e: any) => void;
  emailError: string;
  firstNameError: string;
  lastNameError: string;
  newsletterModalData: any;
}

const FormSection: FC<FormSectionProps> = ({
  handleInputChange,
  handleSubmit,
  emailError,
  firstNameError,
  lastNameError,
  newsletterModalData,
}) => {
  const t = useTranslations();
  return (
    <>
      <ModalHeader>
        {t(newsletterModalData.introHeadline) || t('Subscribe to the E&V Blog')}
      </ModalHeader>
      {newsletterModalData.introCopy && (
        <ModalText>
          <RichText richText={newsletterModalData.introCopy} />
        </ModalText>
      )}
      <form id="subscribe" onSubmit={handleSubmit}>
        <FormField>
          <FormLabel htmlFor="firstName">{t('First Name')}</FormLabel>
          <InputField
            id="firstName"
            placeholder={t('First Name')}
            type="text"
            onChange={(e) => handleInputChange(e)}
          />
          {firstNameError != '' && <ErrorMessage message={firstNameError} />}
        </FormField>

        <FormField>
          <FormLabel htmlFor="lastName">{t('Last Name')}</FormLabel>
          <InputField
            id="lastName"
            placeholder={t('Last Name')}
            onChange={(e) => handleInputChange(e)}
          />
          {lastNameError != '' && <ErrorMessage message={lastNameError} />}
        </FormField>

        <FormField>
          <FormLabel htmlFor="email">{t('Email')}</FormLabel>
          <EmailField
            id="email"
            name="email"
            type="email"
            placeholder={'you@emailaddress.com'}
            onChange={(e) => handleInputChange(e)}
            $isError={emailError != '' ? true : false}
          />
          {emailError != '' && <ErrorMessage message={emailError} />}
        </FormField>
      </form>
      <ImageWrapper>
        <EVImg
          src={newsletterModalData?.introImage?.fields?.file?.url || '/assets/subscribe.png'}
          imageDesc={
            newsletterModalData?.introImage?.fields?.description || 'Woman shopping with coffee'
          }
          objectFit="contain"
          sizes={'225px'}
          priority
        />
      </ImageWrapper>
    </>
  );
};

interface SuccessSectionProps {
  newsletterModalData: {
    successHeadline: string;
    successCopy: string;
    errorCopy: string;
  };
}

const SuccessSection: FC<SuccessSectionProps> = ({ newsletterModalData }) => {
  const { successHeadline, successCopy, errorCopy } = newsletterModalData;
  return (
    <SuccessWrapper>
      <SVGWrapper>
        <Status color="#2E8540" embeddedIcon="success" />
      </SVGWrapper>
      <ModalHeader>{successHeadline || `You're signed up.`}</ModalHeader>
      <SuccessText>
        {errorCopy ? (
          <PlainText>{successCopy}</PlainText>
        ) : (
          `Check your inbox for a confirmation email.`
        )}
      </SuccessText>
    </SuccessWrapper>
  ); //** &nbsp; above prevents widows
};

interface UnsuccessfulSectionProps {
  newsletterModalData: {
    errorHeadline: string;
    errorCopy: string;
  };
}

const UnsuccessfulSection: FC<UnsuccessfulSectionProps> = ({ newsletterModalData }) => {
  const { errorHeadline, errorCopy } = newsletterModalData;

  return (
    <SuccessWrapper>
      <SVGWrapper>
        <Status color="#cd2026" embeddedIcon="error" />
      </SVGWrapper>
      <ModalHeader>{errorHeadline || `Error`}</ModalHeader>
      <SuccessText>
        {errorCopy ? <PlainText>{errorCopy}</PlainText> : 'Please try again.'}
      </SuccessText>
    </SuccessWrapper>
  );
};

export default BlogSubscribeBtn;
